import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import axiosInstance from "../../../../hooks/axiosInstance";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { COUNTRY_CODES } from "../../../../constants/countryCodes";
import "./Style.css";

export default function EditCompany({
    showEdit,
    closeModal,
    refreshPage,
    editSubAccount,
}) {
    const [validationError, setValidationError] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const countryCodes = COUNTRY_CODES;

    const [formData, setFormData] = useState({
        id: "",
        user_id: "",
        first_name: "",
        last_name: "",
        phone: "",
        country_code: "+234",
        email: "",
    });

    // Prepopulate form with editSubAccount data
    useEffect(() => {
        if (editSubAccount) {
            const phone = editSubAccount.account_phone || "";
            let country_code = "+1"; 
            let phone_number = phone;

            // Find matching country code
            const matchedCountry = countryCodes.find((country) =>
                phone.startsWith(country.code)
            );
            if (matchedCountry) {
                country_code = matchedCountry.code;
                phone_number = phone.slice(country_code.length);
            } else if (phone.startsWith("+")) {
                const possibleCode = phone.match(/^\+\d{1,4}/)?.[0];
                if (possibleCode) {
                    country_code = possibleCode;
                    phone_number = phone.slice(possibleCode.length);
                }
            }

            setFormData({
                id: editSubAccount.sub_account_id || "",
                user_id: editSubAccount.user_id || "",
                first_name: editSubAccount.account_first_name || "",
                last_name: editSubAccount.account_last_name || "",
                phone: phone_number,
                country_code,
                email: editSubAccount.account_email || "",
            });
        }
    }, [editSubAccount, countryCodes]);

    const resetForm = () => {
        setFormData({
            id: "",
            user_id: "",
            first_name: "",
            last_name: "",
            phone: "",
            country_code: "+234",
            email: "",
        });
        setValidationError({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "phone") {
            // Allow only numbers for phone
            const numericValue = value.replace(/[^0-9]/g, "");
            setFormData((prev) => ({ ...prev, phone: numericValue }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleClose = () => {
        resetForm();
        closeModal();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const payload = {
                id: formData.id,
                user_id: formData.user_id,
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                phone: `${formData.country_code}${formData.phone}`,
            };

            const response = await axiosInstance.post(`/user/sub_account/sub_accounts`, payload);

            if (response.data.success) {
                toast.success("Sub Account updated successfully!");
                refreshPage();
                handleClose();
            } else {
                toast.error(response.data.message || "Failed to update sub account.");
            }
        } catch (error) {
            console.error("Error updating sub account:", error);
            if (error.response && error.response.data.errors) {
                setValidationError(error.response.data.errors);
                toast.error("Failed to update sub account.");
            } else {
                toast.error("An error occurred while updating the sub account.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="CreateCSP">
            <Modal show={showEdit} onHide={handleClose} dialogClassName="modal-md">
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Sub Account</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3" controlId="formFirstName">
                            <div>
                                <h6>First Name</h6>
                            </div>
                            <Form.Control
                                type="text"
                                placeholder="Enter..."
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                            />
                            {validationError.first_name && (
                                <small className="text-danger my-1">
                                    {validationError.first_name[0]}
                                </small>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formLastName">
                            <div>
                                <h6>Last Name</h6>
                            </div>
                            <Form.Control
                                type="text"
                                placeholder="Enter..."
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                            />
                            {validationError.last_name && (
                                <small className="text-danger my-1">
                                    {validationError.last_name[0]}
                                </small>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formEmail">
                            <div>
                                <h6>Company Email</h6>
                            </div>
                            <Form.Control
                                type="email"
                                placeholder="Enter..."
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                readOnly
                                style={{ backgroundColor: "#f0f0f0", color: "#6c757d" }}
                            />
                            {validationError.email && (
                                <small className="text-danger my-1">
                                    {validationError.email[0]}
                                </small>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formPhone">
                            <div>
                                <h6>Phone</h6>
                            </div>
                            <div className="input-group">
                                <select
                                    name="country_code"
                                    value={formData.country_code}
                                    onChange={handleChange}
                                    className="form-control"
                                    style={{ width: "5%" }}
                                >
                                    {countryCodes.length === 0 ? (
                                        <option>Loading...</option>
                                    ) : (
                                        countryCodes.map((country) => (
                                            <option key={country.code} value={country.code}>
                                                {country.label}
                                            </option>
                                        ))
                                    )}
                                </select>
                                <Form.Control
                                    type="tel"
                                    placeholder="Enter phone number..."
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            {validationError.phone && (
                                <small className="text-danger my-1">
                                    {validationError.phone[0]}
                                </small>
                            )}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            style={{ backgroundColor: "#c4281b", color: "#fff" }}
                            type="submit"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />{" "}
                                    Saving...
                                </>
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}